import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Button,
  Alert,
  TableSortLabel,
  Dialog,
  DialogContent,
  useTheme,
  useMediaQuery,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  CameraAlt as CameraIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import SwipeableCard from './SwipeableCard';
import { useTranslation } from 'react-i18next';
import api from '../api';
import { useAuth } from '../AuthContext';
import { navyBlue } from '../constants/colors';
import { useThemeColors } from '../hooks/useThemeColors';

// Issue Type Chip labels
const getIssueTypeLabel = (assignmentType, t, language) => {
  switch (assignmentType) {
    case 'janitor':
      return t('workOrders.janitorialRequest') || 'Janitorial';
    case 'it_technician':
      return t('workOrders.itIssue') || 'IT/Computer';
    default:
      return t('workOrders.maintenanceIssue') || 'Maintenance';
  }
};

export default function Dashboard() {
  const [workOrders, setWorkOrders] = useState([]);
  const [assets, setAssets] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterType, setFilterType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [openRequestDialog, setOpenRequestDialog] = useState(false);
  const [formData, setFormData] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [completedDateRange, setCompletedDateRange] = useState('7');
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const startY = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t, i18n } = useTranslation();
  const {
    priorityColors,
    statusColors,
    overdueColors,
    issueTypeColors,
    tableRowAlt,
    tableRowHover,
    borderColor,
    pmTypeBackground,
    pmTypeText,
    defaultTypeBackground,
    defaultTypeText,
    hoverBackground,
    accentBlue,
    gradientPrimary,
    gradientPrimaryHover,
  } = useThemeColors();

  const getLocalizedText = (obj, field) => {
    const isSpanish = i18n.language === 'es';
    const spanishField = `${field}_es`;
    if (isSpanish && obj[spanishField]) {
      return obj[spanishField];
    }
    return obj[field];
  };

  const getAssetDescription = (wo) => {
    const isSpanish = i18n.language === 'es';
    if (isSpanish && wo.asset_description_es) {
      return wo.asset_description_es;
    }
    return wo.asset_description;
  };

  useEffect(() => {
    loadData();
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => {
      loadData(true); // silent refresh
    }, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [ordersRes, statsRes, assetsRes] = await Promise.all([
        api.get('/work-orders'),
        api.get('/work-orders/stats/dashboard'),
        api.get('/assets'),
      ]);
      setWorkOrders(ordersRes.data);
      setStats(statsRes.data);
      setAssets(assetsRes.data);
      setLastUpdated(new Date());
    } catch (err) {
      if (!silent) setError(t('dashboard.failedToLoad'));
      console.error(err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Handle manual refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadData(true);
    setIsRefreshing(false);
  };

  // Swipe action handlers
  const handleSwipeComplete = async (woId) => {
    if (!window.confirm(t('dashboard.confirmComplete') || 'Mark this work order as complete?')) return;
    try {
      await api.patch(`/work-orders/${woId}/status`, { status: 'completed' });
      loadData(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to complete work order');
    }
  };

  const handleSwipePriority = (woId) => {
    // Navigate to detail page to change priority
    navigate(`/work-orders/${woId}`);
  };

  // Pull-to-refresh handlers
  const handleTouchStart = (e) => {
    if (containerRef.current && window.scrollY === 0) {
      startY.current = e.touches[0].clientY;
    }
  };

  const handleTouchMove = (e) => {
    if (startY.current !== null && window.scrollY === 0) {
      const currentY = e.touches[0].clientY;
      const distance = currentY - startY.current;
      if (distance > 0 && distance < 150) {
        setPullDistance(distance);
      }
    }
  };

  const handleTouchEnd = async () => {
    if (pullDistance > 80) {
      setIsRefreshing(true);
      await loadData(true);
      setIsRefreshing(false);
    }
    setPullDistance(0);
    startY.current = null;
  };

  const formatChipLabel = (text) => {
    if (!text) return 'N/A';
    if (text === 'preventive_maintenance' || text === 'PM') return t('dashboard.typePM');
    if (text === 'in_progress') return t('dashboard.statusInProgress');
    if (text === 'maintenance_request') return t('dashboard.typeRequest');
    if (text === 'assigned') return t('dashboard.statusAssigned');
    if (text === 'completed') return t('dashboard.statusCompleted');
    if (text === 'low') return t('dashboard.priorityLow');
    if (text === 'medium') return t('dashboard.priorityMedium');
    if (text === 'high') return t('dashboard.priorityHigh');
    if (text === 'emergency') return t('dashboard.priorityEmergency');
    return text.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortData = (data) => {
    return data.sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      if (order === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const handleRemoveFile = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const handleCloseRequestDialog = () => {
    setOpenRequestDialog(false);
    setFormData({});
    setError('');
    handleRemoveFile();
  };

  const handleSubmitRequest = async () => {
    if (!formData.asset_id || !formData.title) {
      setError(t('dashboard.assetAndTitleRequired'));
      return;
    }
    setSubmitting(true);
    setUploadProgress(0);

    try {
      const response = await api.post('/work-orders', formData);
      const workOrderId = response.data.id;

      // Upload photo if selected
      if (selectedFile && workOrderId) {
        const photoData = new FormData();
        photoData.append('file', selectedFile);

        await api.post(`/uploads/work-order/${workOrderId}`, photoData, {
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        });
      }

      handleCloseRequestDialog();
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || t('dashboard.failedToCreate'));
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  // Custom styled chip for priority
  const PriorityChip = ({ priority }) => {
    const colors = priorityColors[priority] || priorityColors.medium;
    return (
      <Chip
        label={formatChipLabel(priority)}
        size="small"
        sx={{
          backgroundColor: colors.bg,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          fontWeight: 600,
        }}
      />
    );
  };

  // Custom styled chip for status
  const StatusChip = ({ status }) => {
    const colors = statusColors[status] || statusColors.assigned;
    return (
      <Chip
        label={formatChipLabel(status)}
        size="small"
        sx={{
          backgroundColor: colors.bg,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          fontWeight: 600,
        }}
      />
    );
  };

  // Custom styled chip for issue type (assignment_type)
  const IssueTypeChip = ({ assignmentType }) => {
    const colors = issueTypeColors[assignmentType] || issueTypeColors.technician;
    const label = getIssueTypeLabel(assignmentType, t, i18n.language);
    return (
      <Chip
        label={label}
        size="small"
        sx={{
          backgroundColor: colors.bg,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          fontWeight: 600,
        }}
      />
    );
  };

  // Toggle button styles
  const toggleButtonStyle = {
    borderRadius: '20px !important',
    border: `1.5px solid ${navyBlue} !important`,
    py: 0.5,
    px: 2,
    fontSize: '0.85rem',
    textTransform: 'none',
    '&.Mui-selected': {
      backgroundColor: navyBlue,
      color: 'white',
      '&:hover': {
        backgroundColor: '#2c4a6e',
      },
    },
    '&:hover': {
      backgroundColor: hoverBackground,
    },
  };

  // Mobile card component
  const WorkOrderCard = ({ wo }) => (
    <Card
      sx={{
        mb: 2,
        cursor: 'pointer',
        borderRadius: 2,
        border: `1px solid ${borderColor}`,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-2px)',
          borderColor: navyBlue,
        },
        '&:active': {
          transform: 'scale(0.98)',
        }
      }}
      onClick={() => navigate(`/work-orders/${wo.id}`)}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
          <Typography variant="h6" component="div" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: accentBlue }}>
            {wo.work_order_number}
          </Typography>
          <StatusChip status={wo.status} />
        </Box>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          <strong>{t('dashboard.asset')}:</strong> {wo.asset_number} - {getAssetDescription(wo)}
        </Typography>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          <strong>{t('dashboard.woTitle')}:</strong> {getLocalizedText(wo, 'title')}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1, mb: 1 }}>
          <Chip
            label={formatChipLabel(wo.type)}
            size="small"
            sx={{
              backgroundColor: wo.type === 'preventive_maintenance' ? pmTypeBackground : defaultTypeBackground,
              color: wo.type === 'preventive_maintenance' ? pmTypeText : defaultTypeText,
              fontWeight: 500,
            }}
          />
          <PriorityChip priority={wo.priority} />
          <IssueTypeChip assignmentType={wo.assignment_type} />
        </Box>

        <Typography variant="caption" color="text.secondary" display="block">
          <strong>{t('dashboard.assignedTo')}:</strong> {wo.assigned_to_name || t('dashboard.unassigned')}
        </Typography>

        <Typography variant="caption" color="text.secondary" display="block">
          <strong>{t('dashboard.created')}:</strong> {wo.created_at ? format(new Date(wo.created_at), 'MM/dd/yyyy') : '-'}
        </Typography>

        {wo.due_date && (
          <Typography
            variant="caption"
            display="block"
            sx={{
              color: new Date(wo.due_date) < new Date() ? overdueColors.text : 'text.secondary',
              fontWeight: new Date(wo.due_date) < new Date() ? 600 : 400,
            }}
          >
            <strong>{t('dashboard.dueDate') || 'Due Date'}:</strong> {format(new Date(wo.due_date), 'MM/dd/yyyy')}
            {new Date(wo.due_date) < new Date() && ' (OVERDUE)'}
          </Typography>
        )}

        {wo.completed_at && (
          <Typography variant="caption" color="text.secondary" display="block">
            <strong>{t('dashboard.completedAt')}:</strong> {format(new Date(wo.completed_at), 'MM/dd/yyyy')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const filteredWorkOrders = workOrders.filter((wo) => {
    const matchesStatus = !filterStatus || wo.status === filterStatus;
    const matchesPriority = !filterPriority || wo.priority === filterPriority;
    const matchesType = !filterType || wo.type === filterType;
    const matchesSearch =
      !searchTerm ||
      wo.work_order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wo.asset_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wo.asset_description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wo.title.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesPriority && matchesType && matchesSearch;
  });

  const openWorkOrders = sortData(filteredWorkOrders.filter((wo) => wo.status !== 'completed'));

  const completedWorkOrders = sortData(
    filteredWorkOrders.filter((wo) => {
      if (wo.status !== 'completed') return false;
      if (completedDateRange === 'all') return true;
      const daysAgo = parseInt(completedDateRange);
      const completedDate = new Date(wo.completed_at || wo.updated_at);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysAgo);
      return completedDate >= cutoffDate;
    })
  );

  if (loading) {
    return (
      <Box>
        <Typography>{t('common.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      onTouchStart={isMobile ? handleTouchStart : undefined}
      onTouchMove={isMobile ? handleTouchMove : undefined}
      onTouchEnd={isMobile ? handleTouchEnd : undefined}
    >
      {/* Pull-to-refresh indicator */}
      {pullDistance > 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 2,
            opacity: Math.min(1, pullDistance / 80),
            transform: `translateY(${pullDistance / 2}px)`,
            transition: 'opacity 0.1s ease',
          }}
        >
          <CircularProgress size={24} sx={{ color: accentBlue }} />
          <Typography variant="caption" display="block" color="textSecondary">
            {pullDistance > 80 ? t('dashboard.releaseToRefresh') || 'Release to refresh' : t('dashboard.pullToRefresh') || 'Pull to refresh'}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" sx={{ color: accentBlue, fontWeight: 700, mb: 0 }}>{t('dashboard.title')}</Typography>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="textSecondary">
              {t('dashboard.lastUpdated') || 'Updated'}: {formatDistanceToNow(lastUpdated, { addSuffix: true })}
            </Typography>
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={isRefreshing}
              sx={{ color: accentBlue }}
            >
              {isRefreshing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Box>
        </Box>
        {hasRole('operator', 'technician', 'it_technician', 'supervisor', 'administrator', 'janitor') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenRequestDialog(true)}
            sx={{
              background: gradientPrimary,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                background: gradientPrimaryHover,
              },
            }}
          >
            {t('dashboard.newRequest')}
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ height: '100%', borderRadius: 2, border: `2px solid ${navyBlue}` }}>
            <CardContent sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2 } }}>
              <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {t('dashboard.totalOpen')}
              </Typography>
              <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, color: accentBlue, fontWeight: 700 }}>
                {stats.total || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={0} md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
          <Card sx={{ height: '100%', borderRadius: 2, border: '2px solid #ff9800' }}>
            <CardContent sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2 } }}>
              <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {t('dashboard.assigned')}
              </Typography>
              <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, color: '#e65100', fontWeight: 700 }}>
                {stats.assigned || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={0} md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
          <Card sx={{ height: '100%', borderRadius: 2, border: '2px solid #2196f3' }}>
            <CardContent sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2 } }}>
              <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {t('dashboard.inProgress')}
              </Typography>
              <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, color: pmTypeText, fontWeight: 700 }}>
                {stats.in_progress || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ height: '100%', borderRadius: 2, border: '2px solid #f44336' }}>
            <CardContent sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2 } }}>
              <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {t('dashboard.highPriority')}
              </Typography>
              <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, color: '#c62828', fontWeight: 700 }}>
                {(stats.high_priority || 0) + (stats.emergency || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        {stats.overdue > 0 && (
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 2, border: `2px solid ${overdueColors.border}`, backgroundColor: overdueColors.bg }}>
              <CardContent sx={{ py: 1.5, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ color: overdueColors.text, fontWeight: 700 }}>
                  {stats.overdue} OVERDUE
                </Typography>
                <Typography variant="body2" sx={{ color: overdueColors.text }}>
                  work order{stats.overdue !== 1 ? 's' : ''} past due date
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Filter Button */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<FilterIcon />}
          onClick={() => setOpenFilterDialog(true)}
          sx={{
            borderColor: navyBlue,
            color: accentBlue,
            borderRadius: 2,
            textTransform: 'none',
            '&:hover': {
              borderColor: navyBlue,
              backgroundColor: hoverBackground,
            },
          }}
        >
          {t('dashboard.filters')}
          {(filterStatus || filterPriority || filterType || searchTerm) && (
            <Chip
              label={(filterStatus ? 1 : 0) + (filterPriority ? 1 : 0) + (filterType ? 1 : 0) + (searchTerm ? 1 : 0)}
              size="small"
              sx={{ ml: 1, backgroundColor: navyBlue, color: 'white' }}
            />
          )}
        </Button>
      </Box>

      {/* Filter Dialog */}
      <Dialog open={openFilterDialog} onClose={() => setOpenFilterDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <Box sx={{ background: gradientPrimary, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            {t('dashboard.filterWorkOrders')}
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('dashboard.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('dashboard.searchPlaceholder')}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
                {t('dashboard.status')}
              </Typography>
              <ToggleButtonGroup
                value={filterStatus}
                exclusive
                onChange={(e, val) => setFilterStatus(val || '')}
                sx={{ flexWrap: 'wrap', gap: 1 }}
              >
                <ToggleButton value="" sx={toggleButtonStyle}>{t('dashboard.all')}</ToggleButton>
                <ToggleButton value="assigned" sx={toggleButtonStyle}>{t('dashboard.statusAssigned')}</ToggleButton>
                <ToggleButton value="in_progress" sx={toggleButtonStyle}>{t('dashboard.statusInProgress')}</ToggleButton>
                <ToggleButton value="completed" sx={toggleButtonStyle}>{t('dashboard.statusCompleted')}</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
                {t('dashboard.priority')}
              </Typography>
              <ToggleButtonGroup
                value={filterPriority}
                exclusive
                onChange={(e, val) => setFilterPriority(val || '')}
                sx={{ flexWrap: 'wrap', gap: 1 }}
              >
                <ToggleButton value="" sx={toggleButtonStyle}>{t('dashboard.all')}</ToggleButton>
                <ToggleButton value="low" sx={{ ...toggleButtonStyle, '&.Mui-selected': { backgroundColor: '#4caf50', color: 'white', borderColor: '#4caf50 !important' } }}>{t('dashboard.priorityLow')}</ToggleButton>
                <ToggleButton value="medium" sx={{ ...toggleButtonStyle, '&.Mui-selected': { backgroundColor: '#2196f3', color: 'white', borderColor: '#2196f3 !important' } }}>{t('dashboard.priorityMedium')}</ToggleButton>
                <ToggleButton value="high" sx={{ ...toggleButtonStyle, '&.Mui-selected': { backgroundColor: '#ff9800', color: 'white', borderColor: '#ff9800 !important' } }}>{t('dashboard.priorityHigh')}</ToggleButton>
                <ToggleButton value="emergency" sx={{ ...toggleButtonStyle, '&.Mui-selected': { backgroundColor: '#f44336', color: 'white', borderColor: '#f44336 !important' } }}>{t('dashboard.priorityEmergency')}</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
                {t('dashboard.type')}
              </Typography>
              <ToggleButtonGroup
                value={filterType}
                exclusive
                onChange={(e, val) => setFilterType(val || '')}
                sx={{ flexWrap: 'wrap', gap: 1 }}
              >
                <ToggleButton value="" sx={toggleButtonStyle}>{t('dashboard.all')}</ToggleButton>
                <ToggleButton value="maintenance_request" sx={toggleButtonStyle}>{t('dashboard.maintenanceRequest')}</ToggleButton>
                <ToggleButton value="preventive_maintenance" sx={toggleButtonStyle}>{t('dashboard.preventiveMaintenance')}</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            {/* Desktop buttons inside grid */}
            {!isMobile && (
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                <Button
                  onClick={() => {
                    setFilterStatus('');
                    setFilterPriority('');
                    setFilterType('');
                    setSearchTerm('');
                  }}
                  size="medium"
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3,
                    py: 1,
                    fontSize: '0.875rem',
                  }}
                >
                  {t('dashboard.clearAll')}
                </Button>
                <Button
                  onClick={() => setOpenFilterDialog(false)}
                  variant="contained"
                  size="medium"
                  sx={{
                    background: gradientPrimary,
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3,
                    py: 1,
                    fontSize: '0.875rem',
                  }}
                >
                  {t('dashboard.applyFilters')}
                </Button>
              </Grid>
            )}
            {/* Spacer for mobile sticky buttons */}
            {isMobile && <Grid item xs={12} sx={{ height: 80 }} />}
          </Grid>
        </DialogContent>
        {/* Mobile sticky buttons */}
        {isMobile && (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              left: 0,
              right: 0,
              bgcolor: 'background.paper',
              borderTop: `1px solid ${borderColor}`,
              p: 2,
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              zIndex: 1,
            }}
          >
            <Button
              onClick={() => {
                setFilterStatus('');
                setFilterPriority('');
                setFilterType('');
                setSearchTerm('');
              }}
              size="large"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                flex: 1,
                maxWidth: 150,
              }}
            >
              {t('dashboard.clearAll')}
            </Button>
            <Button
              onClick={() => setOpenFilterDialog(false)}
              variant="contained"
              size="large"
              sx={{
                background: gradientPrimary,
                borderRadius: 2,
                textTransform: 'none',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                flex: 1,
                maxWidth: 200,
              }}
            >
              {t('dashboard.applyFilters')}
            </Button>
          </Box>
        )}
      </Dialog>

      {/* Work Orders Table */}
      <Typography variant="h5" sx={{ mb: 2, color: accentBlue, fontWeight: 600 }}>{t('dashboard.openWorkOrders')}</Typography>
      {isMobile ? (
        <Box sx={{ mb: 4 }}>
          {openWorkOrders.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
              <Typography color="textSecondary">{t('dashboard.noWorkOrdersFound')}</Typography>
            </Paper>
          ) : (
            openWorkOrders.map((wo) => (
              <SwipeableCard
                key={wo.id}
                onComplete={() => handleSwipeComplete(wo.id)}
                onPriority={() => handleSwipePriority(wo.id)}
                disabled={wo.status === 'completed'}
              >
                <WorkOrderCard wo={wo} />
              </SwipeableCard>
            ))
          )}
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${borderColor}` }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: navyBlue }}>
                  {[
                    { key: 'work_order_number', label: t('dashboard.workOrder') },
                    { key: 'asset_number', label: t('dashboard.asset') },
                    { key: 'title', label: t('dashboard.woTitle') },
                    { key: 'type', label: t('dashboard.type') },
                    { key: 'assignment_type', label: t('workOrders.issueType') || 'Issue Type' },
                    { key: 'priority', label: t('dashboard.priority') },
                    { key: 'status', label: t('dashboard.status') },
                    { key: 'assigned_to_name', label: t('dashboard.assignedTo') },
                    { key: 'due_date', label: t('dashboard.dueDate') || 'Due Date' },
                    { key: 'created_at', label: t('dashboard.created') },
                  ].map((col) => (
                    <TableCell key={col.key} sx={{ color: 'white', fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === col.key}
                        direction={orderBy === col.key ? order : 'asc'}
                        onClick={() => handleRequestSort(col.key)}
                        sx={{
                          color: 'white !important',
                          '& .MuiTableSortLabel-icon': { color: 'white !important' },
                        }}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {openWorkOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography color="textSecondary">{t('dashboard.noWorkOrdersFound')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  openWorkOrders.map((wo, index) => (
                    <TableRow
                      key={wo.id}
                      hover
                      onClick={() => navigate(`/work-orders/${wo.id}`)}
                      sx={{
                        backgroundColor: index % 2 === 0 ? tableRowAlt : 'inherit',
                        '&:hover': { backgroundColor: tableRowHover },
                        cursor: 'pointer',
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" sx={{ color: accentBlue }}>
                          {wo.work_order_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{wo.asset_number}</Typography>
                        <Typography variant="caption" color="textSecondary">{getAssetDescription(wo)}</Typography>
                      </TableCell>
                      <TableCell>{getLocalizedText(wo, 'title')}</TableCell>
                      <TableCell>
                        <Chip
                          label={formatChipLabel(wo.type)}
                          size="small"
                          sx={{
                            backgroundColor: wo.type === 'preventive_maintenance' ? pmTypeBackground : defaultTypeBackground,
                            color: wo.type === 'preventive_maintenance' ? pmTypeText : defaultTypeText,
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                      <TableCell><IssueTypeChip assignmentType={wo.assignment_type} /></TableCell>
                      <TableCell><PriorityChip priority={wo.priority} /></TableCell>
                      <TableCell><StatusChip status={wo.status} /></TableCell>
                      <TableCell>{wo.assigned_to_name || t('dashboard.unassigned')}</TableCell>
                      <TableCell>
                        {wo.due_date ? (
                          <Typography
                            variant="body2"
                            sx={{
                              color: new Date(wo.due_date) < new Date() ? overdueColors.text : 'inherit',
                              fontWeight: new Date(wo.due_date) < new Date() ? 600 : 400,
                            }}
                          >
                            {format(new Date(wo.due_date), 'MMM dd, yyyy')}
                          </Typography>
                        ) : '-'}
                      </TableCell>
                      <TableCell>{format(new Date(wo.created_at), 'MMM dd, yyyy')}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Completed Work Orders Section */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" sx={{ color: accentBlue, fontWeight: 600 }}>{t('dashboard.completedWorkOrders')}</Typography>
          <ToggleButtonGroup
            value={completedDateRange}
            exclusive
            onChange={(e, val) => val && setCompletedDateRange(val)}
            size="small"
            sx={{ flexWrap: 'wrap' }}
          >
            <ToggleButton value="7" sx={toggleButtonStyle}>{t('dashboard.last7Days')}</ToggleButton>
            <ToggleButton value="30" sx={toggleButtonStyle}>{t('dashboard.last30Days')}</ToggleButton>
            <ToggleButton value="90" sx={toggleButtonStyle}>{t('dashboard.last90Days')}</ToggleButton>
            <ToggleButton value="all" sx={toggleButtonStyle}>{t('dashboard.allTime')}</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {isMobile ? (
          <Box>
            {completedWorkOrders.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                <Typography color="textSecondary">{t('dashboard.noCompletedWorkOrders')}</Typography>
              </Paper>
            ) : (
              completedWorkOrders.map((wo) => <WorkOrderCard key={wo.id} wo={wo} />)
            )}
          </Box>
        ) : (
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${borderColor}` }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: navyBlue }}>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('dashboard.workOrder')}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('dashboard.asset')}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('dashboard.woTitle')}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('dashboard.type')}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('workOrders.issueType') || 'Issue Type'}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('dashboard.priority')}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('dashboard.completedBy')}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('dashboard.completedDate')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {completedWorkOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography color="textSecondary">{t('dashboard.noCompletedWorkOrders')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    completedWorkOrders.map((wo, index) => (
                      <TableRow
                        key={wo.id}
                        hover
                        onClick={() => navigate(`/work-orders/${wo.id}`)}
                        sx={{
                          backgroundColor: index % 2 === 0 ? tableRowAlt : 'inherit',
                          '&:hover': { backgroundColor: tableRowHover },
                          cursor: 'pointer',
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" sx={{ color: accentBlue }}>
                            {wo.work_order_number}
                          </Typography>
                        </TableCell>
                        <TableCell>{wo.asset_number || '-'}</TableCell>
                        <TableCell>{getLocalizedText(wo, 'title')}</TableCell>
                        <TableCell>
                          <Chip
                            label={formatChipLabel(wo.type)}
                            size="small"
                            sx={{
                              backgroundColor: wo.type === 'preventive_maintenance' ? pmTypeBackground : defaultTypeBackground,
                              color: wo.type === 'preventive_maintenance' ? pmTypeText : defaultTypeText,
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell><IssueTypeChip assignmentType={wo.assignment_type} /></TableCell>
                        <TableCell><PriorityChip priority={wo.priority} /></TableCell>
                        <TableCell>{wo.assigned_to_name || '-'}</TableCell>
                        <TableCell>
                          {wo.completed_at
                            ? format(new Date(wo.completed_at), 'MMM dd, yyyy')
                            : format(new Date(wo.updated_at), 'MMM dd, yyyy')}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>

      {/* New Request Dialog */}
      <Dialog
        open={openRequestDialog}
        onClose={handleCloseRequestDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <Box sx={{ background: gradientPrimary, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            {t('dashboard.newMaintenanceRequest')}
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Autocomplete
                options={assets}
                getOptionLabel={(asset) => `${asset.asset_number} - ${asset.asset_description}`}
                value={assets.find(a => a.id === formData.asset_id) || null}
                onChange={(event, newValue) => {
                  const isFacilities = newValue?.category === 'Facilities';
                  const isEquipment = newValue?.category === 'Equipment';
                  const hasOptions = isFacilities || isEquipment;
                  setFormData({
                    ...formData,
                    asset_id: newValue ? newValue.id : '',
                    assignment_type: hasOptions ? (formData.assignment_type || 'technician') : 'technician'
                  });
                  // Blur the input to dismiss keyboard on mobile after selection
                  if (newValue && document.activeElement) {
                    document.activeElement.blur();
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label={t('dashboard.assetRequired')} required fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                )}
              />
            </Grid>
            {(() => {
              const selectedAsset = assets.find(a => a.id === formData.asset_id);
              const isFacilities = selectedAsset?.category === 'Facilities';
              const isEquipment = selectedAsset?.category === 'Equipment';
              const showOptions = isFacilities || isEquipment;

              if (!showOptions) return null;

              return (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
                    {t('workOrders.issueType') || 'Issue Type'}
                  </Typography>
                  <ToggleButtonGroup
                    value={formData.assignment_type || 'technician'}
                    exclusive
                    onChange={(e, val) => val && setFormData({ ...formData, assignment_type: val })}
                    sx={{ gap: 1 }}
                  >
                    <ToggleButton value="technician" sx={toggleButtonStyle}>
                      {t('workOrders.maintenanceIssue') || 'Maintenance Issue'}
                    </ToggleButton>
                    {isFacilities && (
                      <ToggleButton value="janitor" sx={{
                        ...toggleButtonStyle,
                        border: '1.5px solid #4caf50 !important',
                        '&.Mui-selected': { backgroundColor: '#4caf50', color: 'white', '&:hover': { backgroundColor: '#66bb6a' } },
                        '&:hover': { backgroundColor: '#e8f5e9' }
                      }}>
                        {t('workOrders.janitorialRequest') || 'Janitorial Request'}
                      </ToggleButton>
                    )}
                    {isEquipment && (
                      <ToggleButton value="it_technician" sx={{
                        ...toggleButtonStyle,
                        border: '1.5px solid #9c27b0 !important',
                        '&.Mui-selected': { backgroundColor: '#9c27b0', color: 'white', '&:hover': { backgroundColor: '#ab47bc' } },
                        '&:hover': { backgroundColor: '#f3e5f5' }
                      }}>
                        {t('workOrders.itIssue') || 'IT/Computer Issue'}
                      </ToggleButton>
                    )}
                  </ToggleButtonGroup>
                </Grid>
              );
            })()}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('dashboard.titleRequired')}
                required
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('dashboard.description')}
                multiline
                rows={4}
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            {/* Photo Upload */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
                {i18n.language === 'es' ? 'Agregar Foto (opcional)' : 'Add Photo (optional)'}
              </Typography>

              {!selectedFile ? (
                <Paper
                  component="label"
                  elevation={0}
                  sx={{
                    border: `2px dashed #9e9e9e`,
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'block',
                    bgcolor: 'transparent',
                    '&:hover': {
                      borderColor: accentBlue,
                      bgcolor: hoverBackground
                    }
                  }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  <CameraIcon sx={{ fontSize: 48, color: '#9e9e9e', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {i18n.language === 'es' ? 'Toca para tomar una foto o seleccionar imagen' : 'Tap to take a photo or select image'}
                  </Typography>
                </Paper>
              ) : (
                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: tableRowAlt, width: '100%', boxSizing: 'border-box' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {previewUrl ? (
                      <Box
                        component="img"
                        src={previewUrl}
                        alt="Preview"
                        sx={{
                          width: 80,
                          height: 80,
                          objectFit: 'cover',
                          borderRadius: 2,
                          flexShrink: 0
                        }}
                      />
                    ) : (
                      <Box sx={{
                        width: 80,
                        height: 80,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: borderColor,
                        borderRadius: 2,
                        flexShrink: 0
                      }}>
                        <ImageIcon sx={{ fontSize: 32, color: '#757575' }} />
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight="medium" noWrap>
                        {selectedFile.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={handleRemoveFile}
                      size="small"
                      sx={{ color: '#f44336', flexShrink: 0 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Paper>
              )}
            </Grid>

            {/* Upload Progress */}
            {submitting && uploadProgress > 0 && uploadProgress < 100 && (
              <Grid item xs={12}>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: borderColor,
                    '& .MuiLinearProgress-bar': {
                      bgcolor: accentBlue,
                      borderRadius: 4
                    }
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
                  {t('dashboard.uploadingPhoto') || 'Uploading photo...'} {uploadProgress}%
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
                {t('dashboard.priority')}
              </Typography>
              <ToggleButtonGroup
                value={formData.priority || 'medium'}
                exclusive
                onChange={(e, val) => val && setFormData({ ...formData, priority: val })}
                sx={{ flexWrap: 'wrap', gap: 1 }}
              >
                <ToggleButton value="low" sx={{ ...toggleButtonStyle, '&.Mui-selected': { backgroundColor: '#4caf50', color: 'white', borderColor: '#4caf50 !important' } }}>
                  {t('dashboard.priorityLow')}
                </ToggleButton>
                <ToggleButton value="medium" sx={{ ...toggleButtonStyle, '&.Mui-selected': { backgroundColor: '#2196f3', color: 'white', borderColor: '#2196f3 !important' } }}>
                  {t('dashboard.priorityMedium')}
                </ToggleButton>
                <ToggleButton value="high" sx={{ ...toggleButtonStyle, '&.Mui-selected': { backgroundColor: '#ff9800', color: 'white', borderColor: '#ff9800 !important' } }}>
                  {t('dashboard.priorityHigh')}
                </ToggleButton>
                <ToggleButton value="emergency" sx={{ ...toggleButtonStyle, '&.Mui-selected': { backgroundColor: '#f44336', color: 'white', borderColor: '#f44336 !important' } }}>
                  {t('dashboard.priorityEmergency')}
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            {/* Desktop buttons inside grid */}
            {!isMobile && (
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                <Button
                  onClick={handleCloseRequestDialog}
                  disabled={submitting}
                  size="medium"
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3,
                    py: 1,
                    fontSize: '0.875rem',
                  }}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={handleSubmitRequest}
                  variant="contained"
                  size="medium"
                  disabled={submitting}
                  sx={{
                    background: gradientPrimary,
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3,
                    py: 1,
                    fontSize: '0.875rem',
                  }}
                >
                  {submitting ? (t('dashboard.submitting') || 'Submitting...') : t('dashboard.submit')}
                </Button>
              </Grid>
            )}
            {/* Spacer for mobile sticky buttons */}
            {isMobile && <Grid item xs={12} sx={{ height: 80 }} />}
          </Grid>
        </DialogContent>
        {/* Mobile sticky buttons */}
        {isMobile && (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              left: 0,
              right: 0,
              bgcolor: 'background.paper',
              borderTop: `1px solid ${borderColor}`,
              p: 2,
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              zIndex: 1,
            }}
          >
            <Button
              onClick={handleCloseRequestDialog}
              disabled={submitting}
              size="large"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                flex: 1,
                maxWidth: 150,
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSubmitRequest}
              variant="contained"
              size="large"
              disabled={submitting}
              sx={{
                background: gradientPrimary,
                borderRadius: 2,
                textTransform: 'none',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                flex: 1,
                maxWidth: 200,
              }}
            >
              {submitting ? (t('dashboard.submitting') || 'Submitting...') : t('dashboard.submit')}
            </Button>
          </Box>
        )}
      </Dialog>
    </Box>
  );
}
